import styled from "styled-components";

export const MeetingPanel = styled.section<{ $dark?: boolean }>`
    display: grid;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid ${p => p.$dark ? "rgba(255,255,255,.14)" : "var(--calendar-border, #d8deec)"};
    border-radius: var(--calendar-radius-card, 12px);
    background: ${p => p.$dark
        ? "linear-gradient(145deg, rgba(91,110,245,.18), rgba(124,58,237,.12))"
        : "linear-gradient(145deg, rgba(91,110,245,.08), rgba(124,58,237,.05))"};
`;

export const MeetingPanelHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

export const MeetingPanelTitle = styled.h3<{ $dark?: boolean }>`
    margin: 0;
    color: ${p => p.$dark ? "#fff" : "var(--calendar-text, #171a22)"};
    font: 900 1rem/1.3 Manrope, Helvetica, sans-serif;
`;

export const MeetingPanelText = styled.p<{ $dark?: boolean }>`
    margin: .25rem 0 0;
    color: ${p => p.$dark ? "rgba(255,255,255,.68)" : "var(--calendar-text-muted, #6a7387)"};
    font: 500 .8125rem/1.45 Manrope, Helvetica, sans-serif;
`;

export const MeetingFieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
    @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

export const MeetingFieldInputWrap = styled.div`
    position: relative;
`;

export const MeetingFieldActions = styled.div`
    position: absolute;
    top: 50%;
    right: .55rem;
    display: flex;
    align-items: center;
    gap: .2rem;
    transform: translateY(-50%);
`;

export const MeetingFieldIconButton = styled.button<{ $feedback?: boolean }>`
    display: inline-grid;
    width: ${p => p.$feedback ? "auto" : "1.8rem"};
    height: 1.8rem;
    padding: ${p => p.$feedback ? "0 .45rem" : "0"};
    place-items: center;
    border: 0;
    border-radius: .4rem;
    background: transparent;
    color: var(--calendar-text-muted, #6a7387);
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? ".42" : "1"};

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible {
        background: rgba(91, 110, 245, .12);
        color: #5b6ef5;
    }

    &:focus-visible {
        outline: 2px solid rgba(91, 110, 245, .45);
        outline-offset: 1px;
    }

    svg {
        width: 1.05rem;
        height: 1.05rem;
    }
`;

export const MeetingCompactActionButton = styled.button<{ $small?: boolean }>`
    width: auto;
    height: ${p => p.$small ? "30px" : "46px"};
    padding: ${p => p.$small ? "0 .75rem" : "0 1rem"};
    border: 1px solid rgba(91, 110, 245, .35);
    border-radius: var(--calendar-radius-control, 8px);
    background: rgba(91, 110, 245, .1);
    color: #5b6ef5;
    cursor: pointer;
    font: 800 .75rem/1.1 Manrope, Helvetica, sans-serif;

    &:hover,
    &:focus-visible {
        background: rgba(91, 110, 245, .18);
    }

    &:focus-visible {
        outline: 2px solid rgba(91, 110, 245, .4);
        outline-offset: 1px;
    }
`;

export const MeetingInlineActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    margin-top: .25rem;
`;

export const MeetingInlineActionText = styled.span`
    color: var(--calendar-text-muted, #6a7387);
    font: 500 .8125rem/1.45 Manrope, Helvetica, sans-serif;
`;

export const MeetingFieldActionRow = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    align-items: flex-end;
    justify-content: flex-end;
`;

export const MeetingChipList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    margin-top: .5rem;
`;

export const MeetingChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    min-height: 2rem;
    padding: .3rem .65rem;
    border-radius: 999px;
    background: rgba(91,110,245,.14);
    color: var(--calendar-text, #171a22);
    font: 800 .75rem/1.2 Manrope, Helvetica, sans-serif;
`;

export const MeetingChipRemove = styled.button<{ $cardCorner?: boolean; $light?: boolean }>`
    position: ${p => p.$cardCorner ? "absolute" : "static"};
    top: ${p => p.$cardCorner ? ".55rem" : "auto"};
    right: ${p => p.$cardCorner ? ".55rem" : "auto"};
    width: 1.25rem;
    height: 1.25rem;
    display: inline-grid;
    flex: 0 0 1.25rem;
    padding: 0;
    place-items: center;
    border: 1px solid ${p => p.$light ? "rgba(255,255,255,.38)" : "rgba(217, 45, 32, .28)"};
    border-radius: 999px;
    background: ${p => p.$light ? "rgba(255,255,255,.12)" : "rgba(217, 45, 32, .12)"};
    color: ${p => p.$light ? "#fff" : "#d92d20"};
    cursor: pointer;
    font: 900 .875rem/1 Manrope, Helvetica, sans-serif;

    &:hover,
    &:focus-visible {
        border-color: ${p => p.$light ? "rgba(255,255,255,.7)" : "rgba(217, 45, 32, .55)"};
        background: ${p => p.$light ? "rgba(255,255,255,.22)" : "rgba(217, 45, 32, .22)"};
        color: ${p => p.$light ? "#fff" : "#b42318"};
    }

    &:focus-visible {
        outline: 2px solid ${p => p.$light ? "rgba(255,255,255,.4)" : "rgba(217, 45, 32, .3)"};
        outline-offset: 1px;
    }
`;

export const MeetingStatusCard = styled.div<{
    $dismissible?: boolean;
    $fullWidth?: boolean;
    $tone?: "info" | "success" | "warning" | "danger";
}>`
    position: relative;
    grid-column: ${p => p.$fullWidth ? "1 / -1" : "auto"};
    padding: ${p => p.$dismissible ? ".875rem 2.5rem .875rem 1rem" : ".875rem 1rem"};
    border-radius: .75rem;
    background: ${p => p.$tone === "success"
        ? "rgba(34,197,94,.14)"
        : p.$tone === "warning" ? "rgba(245,158,11,.18)"
            : p.$tone === "danger" ? "rgba(240,68,56,.14)" : "rgba(91,110,245,.14)"};
    color: inherit;
    font: 600 .8125rem/1.45 Manrope, Helvetica, sans-serif;
`;

export const MeetingParticipantPopoverContent = styled.div`
    position: absolute;
    z-index: 20;
    top: calc(100% + .45rem);
    left: 0;
    width: max-content;
    min-width: 12rem;
    max-width: min(24rem, 75vw);
    max-height: 14rem;
    padding: .75rem;
    overflow: auto;
    border: 1px solid var(--calendar-border, #d8deec);
    border-radius: var(--calendar-radius-control, 8px);
    background: var(--calendar-surface, #fff);
    box-shadow: 0 .75rem 2rem rgba(15, 23, 42, .18);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-.2rem);
    transition: opacity .15s ease, transform .15s ease;
`;

export const MeetingParticipantPopover = styled.span`
    position: relative;
    display: inline-flex;

    &:hover ${MeetingParticipantPopoverContent},
    &:focus-within ${MeetingParticipantPopoverContent} {
        opacity: 1;
        pointer-events: auto;
        transform: translateY(0);
    }
`;

export const MeetingParticipantCount = styled.button`
    padding: 0 .2rem;
    border: 0;
    border-bottom: 1px dashed currentColor;
    background: transparent;
    color: #5b6ef5;
    cursor: help;
    font: inherit;
    font-weight: 800;

    &:focus-visible {
        border-radius: .2rem;
        outline: 2px solid rgba(91, 110, 245, .4);
        outline-offset: 2px;
    }
`;

export const MeetingPage = styled.main`
    min-height: 100vh;
    padding: clamp(1rem, 4vw, 3rem);
    background: radial-gradient(circle at top left, #27346f 0%, #111827 48%, #090b12 100%);
    color: #fff;
    display: grid;
    place-items: center;
`;

export const MeetingPageCard = styled.div`
    width: min(68rem, 100%);
    padding: clamp(1.25rem, 4vw, 2.5rem);
    border: 1px solid rgba(255,255,255,.14);
    border-radius: 1.25rem;
    background: rgba(14,18,30,.88);
    box-shadow: 0 2rem 5rem rgba(0,0,0,.34);
    backdrop-filter: blur(18px);
`;

export const MeetingPrimaryButton = styled.button`
    min-height: 2.8rem;
    padding: .75rem 1rem;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, #5b6ef5, #7c3aed);
    color: #fff;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? .58 : 1};
    font: 900 .875rem/1.2 Manrope, Helvetica, sans-serif;
`;

export const MeetingSecondaryButton = styled.button`
    min-height: 2.8rem;
    padding: .75rem 1rem;
    border: 1px solid rgba(255,255,255,.18);
    border-radius: 999px;
    background: rgba(255,255,255,.08);
    color: #fff;
    cursor: pointer;
    font: 800 .875rem/1.2 Manrope, Helvetica, sans-serif;
`;

export const MeetingStoreLinks = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .75rem;
`;

export const MeetingStoreLink = styled.a`
    display: inline-flex;
    width: auto;
    border-radius: .45rem;
    text-decoration: none;

    &:focus-visible {
        outline: 2px solid #7c8cff;
        outline-offset: 3px;
    }
`;

export const MeetingStoreBadge = styled.img`
    display: block;
    width: auto;
    height: 2.5rem;
`;

export const MeetingTextInput = styled.input`
    width: 100%;
    min-height: 2.75rem;
    box-sizing: border-box;
    border: 1px solid rgba(255,255,255,.18);
    border-radius: .65rem;
    background: rgba(255,255,255,.08);
    color: #fff;
    padding: .7rem .8rem;
    outline: none;
    font: 600 .875rem/1.2 Manrope, Helvetica, sans-serif;
    &::placeholder { color: rgba(255,255,255,.48); }
    &:focus { border-color: #7c8cff; box-shadow: 0 0 0 3px rgba(91,110,245,.18); }
`;

export const MeetingJoinRequestStack = styled.aside`
    position: fixed;
    right: 1.25rem;
    bottom: 1.25rem;
    z-index: 1400;
    display: grid;
    width: min(22rem, calc(100vw - 2rem));
    max-height: calc(100vh - 2.5rem);
    gap: .75rem;
    overflow: auto;
`;

export const MeetingJoinRequestCard = styled.div`
    padding: 1rem;
    border: 1px solid rgba(129, 140, 248, .72);
    border-radius: .75rem;
    background: #111827;
    box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, .42);
    color: #fff;
`;

export const MeetingJoinRequestText = styled.p`
    margin: 0;
    color: #fff;
    font: 800 .875rem/1.4 Manrope, Helvetica, sans-serif;
    overflow-wrap: anywhere;
`;

export const MeetingJoinRequestActions = styled.div`
    display: flex;
    gap: .5rem;
    margin-top: .75rem;
`;

export const MeetingJoinRequestButton = styled.button<{ $tone: "admit" | "decline" }>`
    min-height: 2.25rem;
    padding: .5rem .9rem;
    border: 1px solid ${p => p.$tone === "admit" ? "#4ade80" : "#f87171"};
    border-radius: .5rem;
    background: ${p => p.$tone === "admit" ? "#15803d" : "#b91c1c"};
    color: #fff;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? .6 : 1};
    font: 900 .8125rem/1 Manrope, Helvetica, sans-serif;

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible {
        background: ${p => p.$tone === "admit" ? "#16a34a" : "#dc2626"};
    }

    &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
    }
`;
